/**
 * @module Auth0ProviderAdapter
 * @description OIDC provider adapter implementation for Auth0.
 * Wraps the Auth0 SPA JS SDK to conform to the OIDCProviderAdapter interface.
 */

/* eslint-disable import/extensions */
import { OIDCProviderAdapter } from './oidc-provider-adapter.mjs';
import { PROVIDER_AUTH0 } from './constants.mjs';
import Auth0ClientEx from '../../utils/auth0-client-ex.mst.mjs';
import Auth0ManagementApiBrowser from '../../../utils/auth0-management-api-browser.mjs';

/**
 * Auth0-specific OIDC provider adapter.
 * Wraps the Auth0 SPA JS SDK calls.
 */
class Auth0ProviderAdapter extends OIDCProviderAdapter {
  /**
   * @param {Object} config - Auth0 configuration
   * @param {string} config.domain - Auth0 domain
   * @param {string} config.clientId - Auth0 client ID
   * @param {string} [config.tenant] - Auth0 tenant name
   * @param {string} [config.authServiceBaseUrl] - Auth service base URL for management API
   * @param {string} [config.audience] - API audience
   * @param {string} [config.connection] - Auth0 connection name
   * @param {Object} [config.authorizationParams] - Additional authorization parameters
   */
  constructor(config) {
    super(config);
    this.client = null;
    this._client = new Auth0ClientEx(config.clientId);
    this._managementApi = null;
    this.providerType = PROVIDER_AUTH0;
  }

  /**
   * Create the Auth0 client using the Auth0 SPA JS SDK.
   * @typedef {object} A0ClientAuthorizationParams
   * @property {string} audience - API audience
   * @property {string} connection - Auth0 connection name
   * @property {string} scope - OAuth scopes
   * @property {boolean} [disableCaptcha] - Whether to disable captcha
   * @property {string} [captchaType] - Type of captcha
   * @property {boolean} [enableSso] - Whether SSO is enabled
   * @property {string} [auth0LoginConfigToken] - @deprecated SSAS generates tokens server-side. Client-provided tokens for backwards compat only.
   * @property {string|null} [devIsLocalCdn] - Local CDN script for dev
   * @property {boolean} [shouldRenderRememberMe] - Whether to render remember me
   * @property {string} [workstationAuthTokens] - JSON stringified workstation auth tokens
   * @property {string} [whiteLabelJson] - JSON stringified white label options
   * @property {string} [remember_device] - Device ID for remember me
   * @property {string} [onboardUsername] - Username for onboarding
   * @property {string} [onboardNonce] - Nonce for onboarding
   *
   * @typedef {object} A0ClientOptions
   * @property {string} domain - Auth0 domain
   * @property {string} clientId - Auth0 client ID
   * @property {A0ClientAuthorizationParams} authorizationParams - Authorization parameters
   * @property {string} cacheLocation - Cache location ('localstorage' or 'memory')
   * @property {boolean} useRefreshTokens - Whether to use refresh tokens
   * @property {boolean} useRefreshTokensFallback - Whether to use refresh tokens fallback
   *
   * @param {A0ClientOptions} a0ClientOptions
   * @returns {Promise<this>} The initialized adapter
   */
  async createClient(a0ClientOptions) {
    console.log(`Auth0ProviderAdapter: Creating client with options: ${JSON.stringify(a0ClientOptions)}`);
    this.client = await window.auth0SpaJs.createAuth0Client(a0ClientOptions);
    return this;
  }

  /**
   * Initiate login with redirect using Auth0 SDK.
   * @param {Object} options - Login options
   * @param {Object} [options.authorizationParams] - Authorization parameters
   * @param {Object} [options.appState] - App state to preserve
   * @returns {Promise<void>}
   */
  async loginWithRedirect(options = {}) {
    console.log(`Auth0ProviderAdapter: loginWithRedirect with options: ${JSON.stringify(options)}`);
    return this.client.loginWithRedirect(options);
  }

  /**
   * Handle the Auth0 redirect callback.
   * @returns {Promise<{appState: Object}>} The preserved app state
   */
  async handleRedirectCallback() {
    console.log('Auth0ProviderAdapter: handleRedirectCallback');
    return this.client.handleRedirectCallback();
  }

  /**
   * Check if user is authenticated via Auth0.
   * @returns {Promise<boolean>} True if authenticated
   */
  async isAuthenticated() {
    const isAuth = await this.client.isAuthenticated();
    console.log(`Auth0ProviderAdapter: isAuthenticated => ${isAuth}`);
    return isAuth;
  }

  /**
   * Get an access token silently from Auth0.
   * @param {Object} [options] - Token options
   * @returns {Promise<string>} The access token
   */
  async getTokenSilently(options = {}) {
    console.log('Auth0ProviderAdapter: getToken');
    return this.client.getTokenSilently(options);
  }

  /**
   * Get the user profile from Auth0.
   * @returns {Promise<Object|null>} The user profile
   */
  async getUser() {
    console.log('Auth0ProviderAdapter: getUser');
    return this.client.getUser();
  }

  /**
   * Logout from Auth0.
   * @param {Object} [options] - Logout options
   * @param {Object} [options.logoutParams] - Logout parameters
   * @param {string} [options.logoutParams.returnTo] - Return URL after logout
   * @param {boolean} [options.logoutParams.federated] - Whether to logout from IdP
   * @param {boolean} [options.openUrl] - Whether to redirect (false to logout without redirect)
   * @returns {Promise<void>}
   */
  async logout(options = {}) {
    console.log(`Auth0ProviderAdapter: logout with options: ${JSON.stringify(options)}`);
    return this.client.logout(options);
  }

  /**
   * Get the provider type identifier.
   * @returns {string} 'auth0'
   */
  getProviderType() {
    return 'auth0';
  }

  /**
   * Get the underlying Auth0 client.
   * Useful for accessing Auth0-specific methods not in the adapter interface.
   * @returns {Object} The Auth0 SPA JS client
   */
  getUnderlyingClient() {
    return this.client;
  }

  cleanAuthState() {
    this._client.cleanAuth0State();
  }

  /**
   * Lazy getter for Auth0 Management API instance.
   * @returns {Auth0ManagementApiBrowser}
   */
  get _auth0ManagementApi() {
    if (!this._managementApi) {
      this._managementApi = new Auth0ManagementApiBrowser({
        tenantOrDomain: this.config.tenant,
        clientId: this.config.clientId,
        authServiceBaseUrl: this.config.authServiceBaseUrl,
      });
    }
    return this._managementApi;
  }

  /**
   * Enrich a user profile with additional IDP data from Auth0 Management API.
   * @param {Object} baseUserProfile - The base user profile from getUser()
   * @returns {Promise<Object>} The enriched user profile with idpProps
   */
  async enrichUserProfile(baseUserProfile) {
    try {
      console.log('Auth0ProviderAdapter: enriching user profile with IDP data...');
      baseUserProfile.idpProps = await this._auth0ManagementApi.getUser();
      console.log(`Auth0ProviderAdapter: enriched user profile with IDP data => ${JSON.stringify(baseUserProfile.idpProps)}`);
    } catch (error) {
      console.warn(`Auth0ProviderAdapter: Unable to enrich user profile with IDP data => ${error.message}`);
    }
    return baseUserProfile;
  }

  // just a dev helper function so that the entire link / unlink flow can be tested without leaving this repo
  devUnlinkAllSsoAccounts = async () => {
    const unlinkResp = await this._auth0ManagementApi.unlinkUsers({ unlinkAllUsers: true });
    console.log(`_devUnlinkAllSsoAccounts DONE => ${JSON.stringify(unlinkResp)}`);
  };
}

export { Auth0ProviderAdapter };
